/**
 * Custom Hook: useCreateTimeOffRequest
 * Handles time-off request submission with payload building and API integration
 */

import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store/store'
import {
  createTimeOffRequest,
  CreateTimeOffRequestPayload,
} from '../store/slices/Requestor/createNewRequestSlice'
import {
  buildTimeOffRequestPayload,
  validatePayload,
  PayloadBuilderParams,
} from '../utils/payloadBuilder'

export interface VacationBidParams extends Omit<
  PayloadBuilderParams,
  'nuid' | 'status'
> {
  torRank: number | null,
  manualReqRcvdDate?: string
}

export const useCreateTimeOffRequest = () => {
  const dispatch = useDispatch()

  // Get logged-in user NUID from Redux
  // In useCreateTimeOffRequest.ts
  const reduxNuid = useSelector(
    (state: RootState) =>
      state.employeeInfo.timeOffBalance.data?.employeeNUID || ''
  )
  const nuid =
    reduxNuid ||
    sessionStorage.getItem('userId') ||
    sessionStorage.getItem('nuid') ||
    ''
  // Get submission state from Redux
  const { submitLoading, submitError, submitSuccess } = useSelector(
    (state: RootState) => ({
      submitLoading: state.otherReqDetails?.submitLoading || false,
      submitError: state.otherReqDetails?.submitError || null,
      submitSuccess: state.otherReqDetails?.submitSuccess || false,
    })
  )

  /**
   * Submit time-off request
   * Validates payload and dispatches API call
   */
  const submitRequest = useCallback(
    async (
      params: Omit<PayloadBuilderParams, 'nuid' | 'status'>,
      status: 'SUBMITTED' | 'DRAFT'
    ) => {
      try {
        // Build payload with logged-in user's NUID and status
        const payload = buildTimeOffRequestPayload({
          ...params,
          nuid,
          status,
        })

        // Validate payload (logs to console, returns boolean)
        const isValid = validatePayload(payload)
        if (!isValid) {
          return { success: false, error: 'Payload validation failed' }
        }

        // Dispatch API call
        const result = await dispatch(createTimeOffRequest(payload) as any)

        if (result.meta.requestStatus === 'fulfilled') {
          // Extract ID from response - API returns 'timeOffRequestId', fallback to 'toReqId'
          const timeOffRequestId =
            result.payload.timeOffRequestId || result.payload.toReqId || ''
          return { success: true, timeOffRequestId, toReqId: timeOffRequestId }
        } else {
          return {
            success: false,
            error: result.payload || 'Unknown error occurred',
          }
        }
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to submit request'
        console.error('[submitRequest] Error:', errorMessage)
        return { success: false, error: errorMessage }
      }
    },
    [dispatch, nuid]
  )

  /**
   * Submit as DRAFT
   */
  const submitDraft = useCallback(
    (params: Omit<PayloadBuilderParams, 'nuid' | 'status'>) => {
      return submitRequest(params, 'DRAFT')
    },
    [submitRequest]
  )

  /**
   * Submit as SUBMITTED
   */
  const submitFinal = useCallback(
    (params: Omit<PayloadBuilderParams, 'nuid' | 'status'>) => {
      return submitRequest(params, 'SUBMITTED')
    },
    [submitRequest]
  )

  /**
   * Submit Vacation Bid
   * Same as submitFinal but with: appCode='EMP_VB', toTypeCd='A', torRank
   * Also: isSingleDay='Y', durationFlag='F'
   */
  const submitVacationBid = useCallback(
    async (
      params: VacationBidParams,
      status: 'SUBMITTED' | 'DRAFT' = 'SUBMITTED'
    ) => {
      try {
        const { torRank, ...restParams } = params

        // Build base payload
        const basePayload = buildTimeOffRequestPayload({
          ...restParams,
          nuid,
          status,
        })

        // Override fields for vacation bid
        const payload: CreateTimeOffRequestPayload = {
          ...basePayload,
          appCode: 'EMP_VB',
          isSingleDay: basePayload.isSingleDay,
          torRank,
          timeOffDetails: basePayload.timeOffDetails.map((detail) => ({
            ...detail,
            durationFlag: 'F',
            timeOffAllocations: detail.timeOffAllocations.map((alloc) => ({
              ...alloc,
              toTypeCd: 'A',
            })),
          })),
        }

        // Dispatch API call
        const result = await dispatch(createTimeOffRequest(payload) as any)

        if (result.meta.requestStatus === 'fulfilled') {
          const timeOffRequestId =
            result.payload.timeOffRequestId || result.payload.toReqId || ''
          return { success: true, timeOffRequestId, toReqId: timeOffRequestId }
        }
        return {
          success: false,
          error: result.payload || 'Unknown error occurred',
        }
      } catch (error: any) {
        return {
          success: false,
          error: error.message || 'Failed to submit vacation bid',
        }
      }
    },
    [dispatch, nuid]
  )

  /**
   * Submit as RESUBMITTED
   * Used when user edits and resubmits a returned request
   */
  const submitResubmitted = useCallback(
    (params: Omit<PayloadBuilderParams, 'nuid' | 'status'>) => {
      return submitRequest(params, 'RESUBMITTED' as any)
    },
    [submitRequest]
  )

  /**
   * Submit Resubmitted Vacation Bid
   * Used when user edits and resubmits a returned vacation bid with new dates
   * Same as submitVacationBid but with status='RESUBMITTED'
   */
  const submitResubmittedVacationBid = useCallback(
    async (params: VacationBidParams) => {
      try {
        const { torRank, ...restParams } = params

        // Build base payload with RESUBMITTED status
        const basePayload = buildTimeOffRequestPayload({
          ...restParams,
          nuid,
          status: 'RESUBMITTED' as any,
        })

        // Override fields for vacation bid
        const payload: CreateTimeOffRequestPayload = {
          ...basePayload,
          appCode: 'EMP_VB',
          isSingleDay: basePayload.isSingleDay,
          torRank,
          timeOffDetails: basePayload.timeOffDetails.map((detail) => ({
            ...detail,
            durationFlag: 'F',
            timeOffAllocations: detail.timeOffAllocations.map((alloc) => ({
              ...alloc,
              toTypeCd: 'A',
            })),
          })),
        }

        // Dispatch API call
        const result = await dispatch(createTimeOffRequest(payload) as any)

        if (result.meta.requestStatus === 'fulfilled') {
          const timeOffRequestId =
            result.payload.timeOffRequestId || result.payload.toReqId || ''
          return { success: true, timeOffRequestId, toReqId: timeOffRequestId }
        }
        return {
          success: false,
          error: result.payload || 'Unknown error occurred',
        }
      } catch (error: any) {
        return {
          success: false,
          error: error.message || 'Failed to resubmit vacation bid',
        }
      }
    },
    [dispatch, nuid]
  )

  return {
    submitRequest,
    submitDraft,
    submitFinal,
    submitResubmitted,
    submitVacationBid,
    submitResubmittedVacationBid,
    submitLoading,
    submitError,
    submitSuccess,
    nuid,
  }
}

export type UseCreateTimeOffRequestReturn = ReturnType<
  typeof useCreateTimeOffRequest
>
